import React from "react";
import { NoShow } from "../../hooks/useApi";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { UserX, Users, User, Calendar, Trash2, AlertTriangle } from "lucide-react";

interface NoShowListProps {
  noShows: NoShow[];
  onDelete?: (id: number) => Promise<void>;
  userRole?: string;
}

const NoShowList: React.FC<NoShowListProps> = ({ noShows, onDelete, userRole = "worker" }) => {
  if (!noShows || noShows.length === 0) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg text-center">
        <p className="text-gray-600 dark:text-gray-400">
          No hay registros de No Shows
        </p>
      </div>
    );
  }

  // Calculate totals
  const totalMissingAdults = noShows.reduce((sum, ns) => sum + ns.missingAdults, 0);
  const totalMissingChildren = noShows.reduce((sum, ns) => sum + ns.missingChildren, 0);
  const totalMissing = totalMissingAdults + totalMissingChildren;

  return (
    <div className="space-y-5">
      <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 border border-amber-100 dark:border-amber-800/30">
        <div className="flex flex-wrap justify-between items-center mb-3">
          <h3 className="text-sm font-medium text-amber-800 dark:text-amber-300 flex items-center mb-1 sm:mb-0">
            <AlertTriangle className="w-4 h-4 mr-1.5" />
            Resumen de ausencias
          </h3>
          <span className="bg-amber-100 dark:bg-amber-800/40 text-amber-800 dark:text-amber-300 px-2.5 py-0.5 rounded-full text-xs font-medium">
            Total: {totalMissing} pasajeros
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
          <div className="bg-white dark:bg-gray-800 p-2.5 rounded border border-amber-200 dark:border-amber-900/50 flex items-center justify-between">
            <span className="text-gray-500 dark:text-gray-400 flex items-center">
              <Users className="w-3.5 h-3.5 mr-1.5 text-amber-500 dark:text-amber-400" />
              Adultos ausentes:
            </span>
            <span className="font-medium text-gray-800 dark:text-gray-200">{totalMissingAdults}</span>
          </div>
          <div className="bg-white dark:bg-gray-800 p-2.5 rounded border border-amber-200 dark:border-amber-900/50 flex items-center justify-between">
            <span className="text-gray-500 dark:text-gray-400 flex items-center">
              <User className="w-3.5 h-3.5 mr-1.5 text-amber-500 dark:text-amber-400" />
              Niños ausentes:
            </span>
            <span className="font-medium text-gray-800 dark:text-gray-200">{totalMissingChildren}</span>
          </div>
        </div>
      </div>

      <div className="space-y-4 divide-y divide-gray-200 dark:divide-gray-700">
        {noShows.map((noShow) => (
          <div key={noShow.id} className="pt-4 first:pt-0">
            <div className="flex justify-between items-start mb-3">
              <div>
                <h4 className="font-medium text-gray-900 dark:text-gray-100 flex items-center">
                  <UserX className="w-4 h-4 mr-1.5 text-amber-500 dark:text-amber-400" />
                  No Show {format(parseISO(noShow.fechaRegistro), "d 'de' MMMM, yyyy", { locale: es })}
                </h4>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 flex items-center">
                  <User className="w-3.5 h-3.5 mr-1 text-gray-400 dark:text-gray-500" />
                  Registrado por: {noShow.registradoPor}
                </p>
              </div>
              {onDelete && userRole === "admin" && (
                <button
                  onClick={async () => {
                    if (window.confirm("¿Estás seguro de eliminar este registro de No Show?")) {
                      await onDelete(noShow.id);
                    }
                  }}
                  className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 p-1 rounded-full hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  title="Eliminar registro"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3.5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-500 dark:text-gray-400 flex items-center">
                    <Users className="w-3.5 h-3.5 mr-1.5 text-amber-500 dark:text-amber-400" />
                    Adultos ausentes:
                  </span>
                  <span className="font-medium text-gray-800 dark:text-gray-200">{noShow.missingAdults}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-500 dark:text-gray-400 flex items-center">
                    <User className="w-3.5 h-3.5 mr-1.5 text-amber-500 dark:text-amber-400" />
                    Niños ausentes:
                  </span>
                  <span className="font-medium text-gray-800 dark:text-gray-200">{noShow.missingChildren}</span>
                </div>
              </div>
              
              {noShow.justificacion && (
                <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                  <span className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Justificación:</span>
                  <p className="text-sm text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-900/30 p-2.5 rounded border border-gray-100 dark:border-gray-800">
                    {noShow.justificacion}
                  </p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default NoShowList; 